/**
 * Telas de autenticação: login, cadastro, recuperação de senha e o gate de
 * WhatsApp. É o primeiro contato do usuário com o app.
 *
 * `errors` é indexado pelos MESMOS códigos de `utils/errorMessages.ts`
 * (ERROR_CODE_MAP). O serviço devolve código, a tela devolve texto: por isso a
 * mensagem de erro pode ser traduzida sem tocar em nenhuma regra.
 */
const auth = {
  brand: 'MeuGantt',

  title: {
    login: 'Entrar',
    signup: 'Criar Conta',
    reset: 'Recuperar Senha',
    resetPassword: 'Redefinir Senha',
  },

  field: {
    fullName: 'Nome completo',
    fullNamePlaceholder: 'Digite seu nome',
    whatsapp: 'WhatsApp',
    whatsappPlaceholder: '(11) 98765-4321',
    whatsappHint: 'Usamos para te ajudar a começar e ouvir seu feedback.',
    email: 'Email',
    emailPlaceholder: 'Digite seu email',
    password: 'Senha',
    passwordPlaceholder: 'Digite sua senha',
    newPassword: 'Nova senha',
    newPasswordPlaceholder: 'Digite sua nova senha',
    confirmPassword: 'Confirmar senha',
    confirmPasswordPlaceholder: 'Confirme sua nova senha',
  },

  action: {
    login: 'Entrar',
    signup: 'Criar Conta',
    sendEmail: 'Enviar Email',
    updatePassword: 'Atualizar Senha',
    google: 'Entrar com Google',
    save: 'Continuar',
  },

  loading: {
    login: 'Entrando...',
    signup: 'Criando conta...',
    sending: 'Enviando...',
    saving: 'Salvando...',
    preparing: 'Preparando...',
    updating: 'Atualizando...',
  },

  terms: {
    acceptLead: 'Li e aceito os',
    termsLink: 'Termos de Uso',
    and: 'e a',
    privacyLink: 'Política de Privacidade',
    whatsappConsent: 'Aceito receber contato por WhatsApp do MeuGantt (opcional).',
  },

  switch: {
    noAccount: 'Não tem conta?',
    signupCta: 'Cadastre-se',
    forgotPassword: 'Esqueceu a senha?',
    backToLogin: 'Voltar para login',
    or: 'ou',
  },

  signupSuccess: {
    title: 'Conta criada com sucesso!',
    sentTo: 'Enviamos um email de confirmação para:',
    checkInbox: 'Verifique sua caixa de entrada (e a pasta de spam) e clique no link para ativar sua conta.',
    trialTitle: '14 dias de Professional grátis!',
    trialBody: 'Gantt com dependências, Riscos, Custos, Portfolio, Organizações, Caminho Crítico e muito mais, tudo liberado.',
  },

  resetDone: {
    title: 'Senha alterada com sucesso!',
    body: 'Sua senha foi atualizada. Você já pode fazer login com a nova senha.',
    action: 'Ir para login',
  },

  linkExpired: {
    title: 'Link expirado',
    body: 'O link de recuperação de senha expirou ou já foi utilizado.',
    hint: 'Os links de recuperação são válidos por tempo limitado. Solicite um novo link na tela de login.',
  },

  whatsappGate: {
    title: 'Falta só o seu WhatsApp',
    subtitle: 'Usamos para te ajudar a começar e ouvir seu feedback. Leva 10 segundos.',
    consent: 'Aceito receber dicas e contato sobre o produto pelo WhatsApp.',
  },

  /** Regras de senha, por chave. O teste vive em utils/passwordValidation.ts. */
  passwordRules: {
    minLength: 'Mínimo de {{min}} caracteres',
    uppercase: 'Uma letra maiúscula',
    lowercase: 'Uma letra minúscula',
    number: 'Um número',
  },

  /** Mensagens de validação que a própria tela produz. */
  validation: {
    invalidWhatsapp: 'Informe um WhatsApp válido com DDD, ex.: (11) 98765-4321.',
    mustAcceptTerms: 'Você precisa aceitar os Termos de Uso e Política de Privacidade.',
    weakPassword: 'Senha não atende: {{failures}}',
    passwordMismatch: 'As senhas não coincidem',
  },

  /** Indexado pelos códigos de ERROR_CODE_MAP em utils/errorMessages.ts. */
  errors: {
    invalid_credentials: 'Email ou senha incorretos.',
    email_not_confirmed: 'Email não confirmado. Verifique sua caixa de entrada.',
    rate_limit: 'Muitas tentativas. Aguarde alguns minutos.',
    user_already_registered: 'Este email já está cadastrado.',
    weak_password: 'Senha não atende aos requisitos mínimos.',
    email_rate_limit: 'Limite de emails atingido. Tente novamente mais tarde.',
    rate_limit_security: 'Por segurança, aguarde antes de tentar novamente.',
    password_unchanged: 'A nova senha deve ser diferente da anterior.',
    session_missing: 'Sessão expirada. Faça login novamente.',
    token_expired: 'Link expirado. Solicite um novo.',
    access_denied: 'Acesso negado.',
    user_not_found: 'Usuário não encontrado.',
    invalid_link: 'Link inválido ou expirado.',
    invalid_email: 'Endereço de email inválido.',
    provider_disabled: 'Este método de login não está habilitado.',
    captcha_failed: 'Verificação de segurança falhou. Recarregue a página e tente novamente.',
    captcha_verification_failed: 'Verificação de segurança falhou. Tente novamente.',
    network: 'Falha de conexão. Verifique sua internet e tente novamente.',
    unknown: 'Erro inesperado. Tente novamente.',
    loginFailed: 'Email ou senha inválidos',
    signupFailed: 'Erro ao criar conta',
    resetFailed: 'Erro ao enviar email de recuperação',
    oauthFailed: 'Erro ao conectar com provedor',
    tooManyAttempts: 'Muitas tentativas. Aguarde um momento.',
    generic: 'Ocorreu um erro. Tente novamente.',
    saveFailed: 'Não foi possível salvar agora. Tente novamente.',
    linkExpiredLong: 'Link expirado ou inválido. Solicite um novo link de recuperação.',
    samePassword: 'A nova senha deve ser diferente da senha atual.',
  },

  recoverySent: 'Email de recuperação enviado! Verifique sua caixa de entrada.',
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type AuthCopy = typeof auth;

export default auth;
